import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CoreService } from "../../../services/core.service";
import { catchError, take } from "rxjs/operators";
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { of } from "rxjs";

@Component({
  selector: "app-social-ai-alerts-chart",
  standalone: true,
  imports: [CommonModule, MatPaginator, MatPaginatorModule, MatIconModule],
  templateUrl: "./social-ai-alerts-chart.component.html",
  styleUrl: "./social-ai-alerts-chart.component.scss",
})
export class SocialAiAlertsChartComponent implements OnInit, OnChanges {
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedAlertType: string | null = null;
  @Input() pages: any[] = [];
  @Input() isPageAlerts: boolean = false;


  companyID: number | null = null;
  postAlerts: any[] = [];
  accountAlerts: any[] = [];
  isLoading = false;
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  isMobile = window.innerWidth <= 768;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(private coreService: CoreService) { }

  ngOnInit(): void {
    // this.loadAlerts(1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedSiteId'] ||
      changes['selectedPlatformId'] ||
      changes['selectedAlertType']
    ) {
      this.currentPage = 1;

      if (this.selectedSiteId && this.selectedPlatformId) {
        this.loadAlerts(1);
      }
    }
  }

  loadAlerts(page: number = 1): void {
    this.companyID = this.coreService.user?.companyID || 0;
    if (!this.companyID) return;

    this.isLoading = true;
    this.currentPage = page;

    const payload: any = {
      company_id: this.companyID,
      contentsite_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      page
    };

    if (this.selectedAlertType) {
      if (this.isPageAlerts) {
        payload.type = this.selectedAlertType;
      }
    }

    this.coreService
      .postData(`alerts/${this.companyID}`, payload)
      .pipe(
        take(1),
        catchError((err) => {
          console.error("Error fetching alerts:", err);
          this.isLoading = false;
          return of({
            data: [],
            total: 0,
            per_page: 12,
            current_page: 1,
            last_page: 1,
          });
        })
      )
      .subscribe((res: any) => {
        const rawData = res?.data || [];

        this.totalItems = res.total;
        this.pageSize = res.per_page;
        this.currentPage = res.current_page;

        this.postAlerts = rawData.map((item: any) => this.mapAlert(item));
        this.isLoading = false;

        if (this.paginator) {
          this.paginator.length = res.total;
          this.paginator.pageSize = res.per_page;
          this.paginator.pageIndex = res.current_page - 1;
        }
      });
  }


  onPageChange(event: PageEvent) {
    const newPage = event.pageIndex + 1;
    const maxPage = Math.ceil(this.totalItems / this.pageSize);

    if (newPage < 1 || newPage > maxPage) return;

    this.loadAlerts(newPage);
  }

  private mapAlert(item: any) {
    const alertText = item.alert_text || "";
    const alertType = item.alert_type;

    // --- Icon & Color ---
    let color = "text-gray-600"

    if (alertType === "success") {
      color = "text-green-600";

    } else if (alertType === "warning") {
      color = "text-yellow-600";

    } else if (alertType === "danger") {
      color = "text-red-600";

    }

    // --- Parse text ---
    const deltaMatch = alertText.match(/\(([-+]?\d+(\.\d+)?)%\)/); // catches both + and - percentages
    const metricMatch = alertText.match(/'([^']+)'/);
    const valuesMatch = alertText.match(/\((\d+)\s*→\s*(\d+)\)/);

    const numberFormatter = new Intl.NumberFormat("en-US");
    const periodMatch = alertText.match(/\((week|days_28)\)/);

    const delta = deltaMatch ? `${deltaMatch[1]}%` : "";
    const metricType = metricMatch ? metricMatch[1] : "";
    const oldValue = valuesMatch
      ? numberFormatter.format(Number(valuesMatch[1]))
      : "";
    const newValue = valuesMatch
      ? numberFormatter.format(Number(valuesMatch[2]))
      : "";

    let period = "";
    if (periodMatch?.[1] === "week") period = "7d";
    else if (periodMatch?.[1] === "days_28") period = "30d";

    // --- Clean domain for thumbnail ---
    const cleanDomain = item.Domain?.replace("https://www.", "")
      .replace("https://", "")
      .replace("http://", "")
      .replace("www.", "")
      .split("/")[0];

    return {
      id: item.id,
      platform: item.platform_name || item.platform || "",
      domain: cleanDomain,
      thumbnail: item.thumbnail_file_path,
      title: metricType.replaceAll("_", " "),
      mainText: alertText.split(" in ")[0],
      delta,
      oldValue,
      newValue,
      period,
      color,
      datetime: new Date(item.created_at).toLocaleString("en-GB"),
      alert_type: alertType,
    };
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = "assets/thumbs/default.png";
  }

  dismissAlert(alertId: number): void {
    if (!this.companyID) return;

    this.coreService
      .postData(`alerts/${this.companyID}/dismiss`, { id: alertId })
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error dismissing alert:', err);
          return of(null);
        })
      )
      .subscribe((res: any) => {
        if (res && res.success) {
          this.postAlerts = this.postAlerts.filter(a => a.id !== alertId);
          this.totalItems--;

          if (this.paginator) {
            this.paginator.length = this.totalItems;
            this.paginator._changePageSize(this.paginator.pageSize);
          }

          if (this.postAlerts.length === 0 && this.currentPage > 1) {
            this.loadAlerts(this.currentPage - 1);
          }

          this.coreService.showSnackbar('Alert dismissed successfully!');
        }
      });
  }
}
